/**
 * Newsbot API client
 *
 * Uses Supabase session token for authentication.
 * No fallback user IDs - auth is always required.
 */

import { supabase } from '../lib/supabase';

const API_BASE = '/api/v1';

class APIClient {
  private cachedToken: string | null = null;
  private cacheExpiresAt = 0;

  private async getAuthHeaders(): Promise<HeadersInit> {
    // Use cached token if still valid (5-minute TTL)
    if (this.cachedToken && Date.now() < this.cacheExpiresAt) {
      return {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${this.cachedToken}`,
      };
    }

    const { data: { session } } = await supabase.auth.getSession();

    if (!session?.access_token) {
      this.cachedToken = null;
      throw new Error('Not authenticated');
    }

    this.cachedToken = session.access_token;
    this.cacheExpiresAt = Date.now() + 5 * 60 * 1000;

    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${session.access_token}`,
    };
  }

  async raw(endpoint: string, options: RequestInit = {}): Promise<Response> {
    const authHeaders = await this.getAuthHeaders();
    const headers = new Headers(authHeaders);
    new Headers(options.headers).forEach((value, key) => headers.set(key, value));
    const response = await fetch(`${API_BASE}${endpoint}`, {
      ...options,
      headers,
    });

    if (!response.ok) {
      if (response.status === 401) {
        this.cachedToken = null;
        this.cacheExpiresAt = 0;
      }
      const error = await response.clone().json().catch(() => ({ detail: response.statusText }));
      throw new Error(error.detail || `HTTP ${response.status}`);
    }

    return response;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 10_000);

    try {
      const response = await this.raw(endpoint, {
        ...options,
        signal: controller.signal,
      });

      if (response.status === 204) {
        return undefined as T;
      }

      return response.json();
    } catch (err) {
      if (err instanceof DOMException && err.name === 'AbortError') {
        throw new Error('Request timed out after 10 seconds', { cause: err });
      }
      throw err;
    } finally {
      clearTimeout(timeoutId);
    }
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    });
  }
}

export const api = new APIClient();
